import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import api, { markActivity, IDLE_MESSAGE } from "@/lib/api";
import { IdleWatcher } from "@/components/IdleWatcher";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = loading, false = unauthenticated
  const [loading, setLoading] = useState(true);

  const fetchMe = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (localStorage.getItem("token")) {
      fetchMe();
    } else {
      setUser(false);
      setLoading(false);
    }
  }, [fetchMe]);

  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === "token" && !e.newValue) { setUser(false); }
      if (e.key === "token" && e.newValue) { fetchMe(); }
    };
    const onIdle = (e) => {
      localStorage.removeItem("token");
      setUser((u) => { if (u !== false) { toast.info(e.detail || IDLE_MESSAGE); } return false; });
    };
    window.addEventListener("storage", onStorage);
    window.addEventListener("idle-logout", onIdle);
    return () => {
      window.removeEventListener("storage", onStorage);
      window.removeEventListener("idle-logout", onIdle);
    };
  }, [fetchMe]);

  const login = async (staff_id, password) => {
    const { data } = await api.post("/auth/login", { ...(staff_id.includes("@") ? { email: staff_id } : { staff_id }), password });
    markActivity();
    localStorage.setItem("token", data.token);
    setUser(data.user);
    return data.user;
  };

  const register = async (values) => {
    const { data } = await api.post("/auth/register", values);
    return data;
  };

  const verifyEmail = async (verification_token, code) => {
    const { data } = await api.post("/auth/verify-email", { verification_token, code });
    markActivity();
    localStorage.setItem("token", data.token);
    setUser(data.user);
    return data.user;
  };

  const logout = useCallback(async () => {
    try { await api.post("/auth/logout"); } catch {}
    localStorage.removeItem("token");
    setUser(false);
  }, []);

  const refreshUser = useCallback(async () => {
    const { data } = await api.get("/auth/me");
    setUser(data);
    return data;
  }, []);

  return (
    <AuthContext.Provider value={{ user, setUser, loading, login, register, verifyEmail, logout, refreshUser }}>
      {children}
      {user && <IdleWatcher onTimeout={logout} />}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
